/**
 * ProfileView — HALAMAN SETTING, RUPA LAMA (route /profile/*)
 * ============================================================
 * Ini SEPARUH pertama daripada sepasang, corak yang sama seperti
 * ExtensionView/ExtensionV39View dan MarketplaceView/…V39View:
 *
 *   suis config/ui_v39 MATI  →  fail ini            (rupa lama)
 *   suis config/ui_v39 HIDUP →  SettingsV39View.jsx (rupa v3.9)
 *
 * Halaman ini TIDAK dipadam apabila v3.9 lancar — itulah yang
 * menjadikan patah balik percuma: matikan suis dan ia kembali
 * serta-merta, tanpa deploy.
 *
 * ⛔ TIADA logik Firebase dalam fail ini. Semua keadaan dan operasi
 *    (tukar emel, kata laluan, avatar, storage, probe extension)
 *    datang daripada useAccountSettings — satu otak, dua rupa.
 *    Kalau perangai perlu diubah, ubah di HOOK, bukan di sini.
 *
 * Susunan diambil daripada app.cal.com/settings, TANPA halaman
 * "Overview" (keputusan user 2026-08-10): setting mengambil alih
 * satu skrin penuh, menu dikumpul di kiri, klik Settings terus
 * mendarat di Profil.
 *
 * ⛔ DIBUANG 2026-08-10 atas arahan user — JANGAN pasang balik
 *    tanpa membina fungsinya dahulu:
 *      · Butang "Delete Account" — dulu tiada onClick langsung.
 *        Kalau nak bina betul, ia WAJIB padam subdoc `detail/text`
 *        setiap produk (Split V2) atau teks produk jadi yatim.
 *      · 3 toggle notifikasi — dulu `defaultChecked` sahaja, tiada
 *        state, tiada simpan, tetapi ada ayat "akan disimpan secara
 *        automatik" di bawahnya.
 *
 * Alamat lama kekal hidup: `/profile/notifications` → Paparan.
 */

import React, { useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
    User, Mail, ShieldCheck, Crown, Zap, CreditCard,
    LogOut, Clock, ChevronRight, ArrowLeft,
    Calendar, RefreshCw, CheckCircle, AlertCircle, Sparkles,
    Eye, EyeOff, Lock, Shield, Send, Camera, Trash2, Sun, Moon,
    AlertTriangle, Pencil, Save, X, Info, Users, MessageCircle,
    Puzzle, HardDrive, Gauge, Download, Palette
} from 'lucide-react';
import { APP_VERSION } from '../version';
import {
    useAccountSettings, fmtBytes,
    STORAGE_LIMIT_BYTES, DAILY_PAID_CAP, TRIAL_COIN_CAP, EXTENSION_ZIP_URL,
} from '../hooks/useAccountSettings';

/* Alamat lama → halaman baharu. Bookmark & link lama mesti kekal hidup. */
const TAB_ALIAS = { notifications: 'appearance' };

const PAGE_META = {
    account: { title: 'Profil', desc: 'Maklumat akaun anda dan cara kami menghubungi anda.' },
    appearance: { title: 'Paparan', desc: 'Pilih tema paparan untuk halaman ini.' },
    security: { title: 'Kata laluan', desc: 'Kata laluan yang sama digunakan untuk webapp dan Extension SESAAT.' },
    billing: { title: 'Pakej saya', desc: 'Pelan langganan dan tarikh tamat tempoh anda.' },
    storage: { title: 'Storage & kuota', desc: 'Ruang simpanan video dan baki generation hari ini.' },
    extension: { title: 'Extension', desc: 'Status pemasangan Extension SESAAT pada browser ini.' },
    referral: { title: 'Ejen rujukan', desc: 'Ejen yang membawa anda ke SESAAT.' },
};

/* Kad standard: tajuk + penerangan + isi + bar bawah pilihan (corak cal.com). */
const SettingsCard = ({ title, desc, children, footer, tone = 'default' }) => (
    <div className={`rounded-2xl border overflow-hidden ${tone === 'warn'
        ? 'bg-amber-50/60 dark:bg-amber-900/10 border-amber-200/70 dark:border-amber-900/40'
        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'
        }`} style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="p-6">
            {title && <h2 className="text-base font-bold text-slate-800 dark:text-white">{title}</h2>}
            {desc && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{desc}</p>}
            {children && <div className={title || desc ? 'mt-5' : ''}>{children}</div>}
        </div>
        {footer && (
            <div className="px-6 py-3.5 bg-slate-50 dark:bg-slate-800/30 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-3">
                {footer}
            </div>
        )}
    </div>
);

/* Baris maklumat baca-sahaja. */
const InfoRow = ({ label, icon, children, tone = 'default' }) => (
    <div>
        <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">{label}</label>
        <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border ${tone === 'brand'
            ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800'
            : tone === 'gold'
                ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700'
            }`}>
            {icon}
            {children}
        </div>
    </div>
);

/* Bar penggunaan (storage / kuota). Warna senyap di bawah 70%, oren melepasi
   70%, merah melepasi 90% — konvensyen sama dengan bar storage pada sidebar. */
const UsageBar = ({ ratio }) => {
    const pct = Math.min(100, Math.max(0, ratio * 100));
    const colour = ratio > 0.9 ? 'bg-rose-500' : ratio > 0.7 ? 'bg-amber-500' : 'bg-indigo-500';
    return (
        <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${colour}`} style={{ width: `${pct}%` }} />
        </div>
    );
};

const ProfileView = ({ initialTab = null }) => {
    const s = useAccountSettings();

    const navigate = useNavigate();
    const location = useLocation();
    const hashTab = location.hash === '#security' ? 'security' : 'account';
    const rawTab = initialTab || hashTab;
    const activeTab = TAB_ALIAS[rawTab] || rawTab;

    // Alamat lama `/profile` dan `/profile#security` ditukar ke alamat halaman
    // sebenar. `/profile/notifications` (bookmark lama) dipetakan ke Paparan.
    useEffect(() => {
        if (!initialTab) { navigate(`/profile/${hashTab}`, { replace: true }); return; }
        if (TAB_ALIAS[initialTab]) navigate(`/profile/${TAB_ALIAS[initialTab]}`, { replace: true });
    }, [initialTab, hashTab, navigate]);

    useEffect(() => {
        if (activeTab !== 'extension') return;
        s.runExtProbe();
    }, [activeTab, s.runExtProbe]); // eslint-disable-line react-hooks/exhaustive-deps

    const tierConfig = {
        admin: { label: 'System Admin', icon: ShieldCheck, textColor: 'text-indigo-600 dark:text-indigo-400' },
        moderator: { label: 'Moderator', icon: Shield, textColor: 'text-purple-600 dark:text-purple-400' },
        paid: { label: 'Paid Client', icon: Crown, textColor: 'text-amber-600 dark:text-amber-400' },
        free: { label: 'Free', icon: Zap, textColor: 'text-slate-500' },
    };
    const currentTier = tierConfig[s.tier] || tierConfig.free;
    const TierIcon = currentTier.icon;
    /* Slot + modul ditarik keluar daripada objek tetapan supaya JSX di bawah
       tidak menambah amaran palsu react-hooks/refs (peraturan itu membaca
       setiap capaian s.xxx dalam JSX sebagai capaian ref — 184 amaran sedia ada). */
    const { browserDigunakan, kuotaSlot, slotTanpaHad, avatarDigunakan, avatarKuota, modulSaya } = s;
    const berbayarBukanAdmin = s.isPaid && !s.isAdmin;

    const strengthColor = s.passwordStrength.score <= 1 ? 'bg-rose-500'
        : s.passwordStrength.score <= 2 ? 'bg-orange-500'
            : s.passwordStrength.score <= 3 ? 'bg-amber-500'
                : s.passwordStrength.score <= 4 ? 'bg-emerald-500' : 'bg-green-500';

    /* Struktur menu SAMA dengan rupa v3.9 (SettingsV39View) — label kumpulan
       kecil-huruf-besar, ikon pada BARIS bukan pada label. Dua rupa mesti
       berkongsi struktur; yang berbeza hanya warna. Kumpulan "Rujukan"
       disorok daripada admin kerana admin memang tiada ejen rujukan.
       ⚠️ "Paparan" wujud di sini sahaja — v3.9 gelap sahaja, jadi pemilih
          tema tiada makna di sana. */
    const navGroups = [
        {
            id: 'akaun', label: 'Akaun', items: [
                { id: 'account', label: 'Profil', icon: User },
                { id: 'appearance', label: 'Paparan', icon: Palette },
            ]
        },
        { id: 'keselamatan', label: 'Keselamatan', items: [{ id: 'security', label: 'Kata laluan', icon: Shield }] },
        {
            id: 'langganan', label: 'Langganan', items: [
                { id: 'billing', label: 'Pakej saya', icon: CreditCard },
                { id: 'storage', label: 'Storage & kuota', icon: HardDrive },
            ]
        },
        { id: 'ext', label: 'Extension', items: [{ id: 'extension', label: 'Status extension', icon: Puzzle }] },
        ...(s.isAdmin ? [] : [{ id: 'rujukan', label: 'Rujukan', items: [{ id: 'referral', label: 'Ejen rujukan', icon: Users }] }]),
    ];

    const meta = PAGE_META[activeTab] || PAGE_META.account;

    if (s.loading) {
        return (
            <div className="flex-1 flex items-center justify-center bg-slate-50 dark:bg-slate-950">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-10 h-10 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin"></div>
                    <p className="text-xs text-slate-400">Memuatkan setting...</p>
                </div>
            </div>
        );
    }

    /* Baris menu — saiz DISALIN daripada NavRow v3.9 (ui.css `.s-navrow`):
       tinggi 32px · padding 0 8px · radius 10px · teks 14px/500 · jurang 10px.
       Warna sahaja yang ikut tema lama.
       ⭐ <Link>, bukan <button>: baris menu mesti pautan sebenar supaya klik
       tengah / Ctrl+klik / "Open link in new tab" berfungsi. */
    const NavItem = ({ item }) => {
        const isActive = activeTab === item.id;
        const Ikon = item.icon;
        return (
            <Link
                to={`/profile/${item.id}`}
                style={{ height: 32, padding: '0 8px', borderRadius: 10, gap: 10, textDecoration: 'none' }}
                className={`w-full flex items-center text-left text-sm font-medium transition-colors ${isActive
                    ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white'
                    : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100/70 dark:hover:bg-slate-800/60 hover:text-slate-800 dark:hover:text-slate-200'
                    }`}
            >
                {Ikon && <Ikon size={16} strokeWidth={1.9} style={{ flex: 'none', opacity: .85 }} />}
                <span className="flex-1 min-w-0 truncate">{item.label}</span>
            </Link>
        );
    };

    return (
        // ★ Setting mengambil alih skrin penuh (macam cal.com) — rel menu utama
        //   app duduk di belakangnya. z-40 sengaja LEBIH RENDAH daripada modal
        //   pengesahan (z-100) supaya modal itu kekal di atas.
        <div className="fixed inset-0 z-40 flex bg-slate-50 dark:bg-slate-950"
            style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>

            {/* ================= REL MENU SETTING ================= */}
            {/* Rel menu — UKURAN disalin daripada rel StudioShell v3.9 supaya
                dua rupa setting benar-benar sama bentuk:
                  lebar 264 · garis kanan 1px · padding 16px 12px 14px
                  jurang antara kumpulan 18 · jurang antara baris 2
                  label kumpulan 10px/700/.08em, padding 0 8px
                  ikon baris 16px strokeWidth 1.9 · baris 32px (lihat NavItem)
                Hanya WARNA yang ikut tema lama. */}
            <aside
                style={{ width: 264, padding: '16px 12px 14px', gap: 18 }}
                className="hidden md:flex flex-shrink-0 flex-col overflow-y-auto overflow-x-hidden min-w-0 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">

                {/* Kepala — satu tindakan di kiri, avatar di hujung kanan */}
                <div className="flex items-center gap-2" style={{ padding: '0 4px' }}>
                    <Link to="/dashboard" style={{ textDecoration: 'none' }}
                        className="flex items-center gap-2 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors">
                        <ArrowLeft size={16} strokeWidth={1.9} /> Kembali
                    </Link>
                    <span className="flex-1" />
                    <span className="w-[22px] h-[22px] rounded-full overflow-hidden flex-none flex items-center justify-center bg-gradient-to-br from-slate-400 to-slate-500 text-white text-[11px] font-semibold">
                        {s.currentPhotoURL
                            ? <img src={s.currentPhotoURL} alt="" referrerPolicy="no-referrer" className="w-full h-full object-cover" />
                            : s.initials}
                    </span>
                </div>

                {navGroups.map(group => (
                    <div key={group.id} className="flex flex-col" style={{ gap: 2 }}>
                        <span
                            style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.08em', padding: '0 8px', marginBottom: 4 }}
                            className="uppercase text-slate-400 dark:text-slate-500">
                            {group.label}
                        </span>
                        {group.items.map(item => <NavItem key={item.id} item={item} />)}
                    </div>
                ))}

                <div className="mt-auto flex flex-col" style={{ gap: 2 }}>
                    <button onClick={s.handleLogout} disabled={s.loggingOut}
                        style={{ height: 32, padding: '0 8px', borderRadius: 10, gap: 10 }}
                        className="w-full flex items-center text-left text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-100/70 dark:hover:bg-slate-800/60 hover:text-slate-800 dark:hover:text-slate-200 transition-colors">
                        {s.loggingOut
                            ? <RefreshCw size={16} strokeWidth={1.9} className="animate-spin flex-none" />
                            : <LogOut size={16} strokeWidth={1.9} className="flex-none opacity-85" />}
                        Log keluar
                    </button>
                    <p style={{ fontSize: 11, margin: '10px 0 0', padding: '0 8px' }} className="text-slate-400 dark:text-slate-600">
                        © 2026 Sesaat · v{APP_VERSION}
                    </p>
                </div>
            </aside>

            {/* ================= KANDUNGAN ================= */}
            <main className="flex-1 overflow-y-auto">

                {/* Menu mendatar — hanya pada skrin kecil, ganti rel kiri */}
                <div className="md:hidden sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
                    <div className="flex items-center gap-2 px-4 py-2.5">
                        <Link to="/dashboard" style={{ textDecoration: 'none' }}
                            className="flex items-center gap-1.5 text-sm font-medium text-slate-500 dark:text-slate-400">
                            <ArrowLeft size={16} /> Kembali
                        </Link>
                    </div>
                    <nav className="flex items-center gap-1.5 px-3 pb-2.5 overflow-x-auto">
                        {navGroups.flatMap(g => g.items).map(item => (
                            <Link key={item.id} to={`/profile/${item.id}`} style={{ textDecoration: 'none' }}
                                className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${activeTab === item.id
                                    ? 'bg-indigo-500 text-white'
                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                                    }`}>
                                {item.label}
                            </Link>
                        ))}
                    </nav>
                </div>

                <div className="max-w-2xl mx-auto px-5 sm:px-8 py-8 sm:py-10">

                    <div className="mb-7">
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{meta.title}</h1>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{meta.desc}</p>
                    </div>

                    <div className="space-y-5">

                        {/* ================= PROFIL ================= */}
                        {activeTab === 'account' && (
                            <>
                                <SettingsCard>
                                    <div className="flex items-center gap-5">
                                        <div className="relative flex-shrink-0">
                                            <div className={`w-16 h-16 rounded-full overflow-hidden flex items-center justify-center text-white text-lg font-bold ${!s.currentPhotoURL ? 'bg-gradient-to-br from-slate-400 to-slate-500' : ''}`}>
                                                {s.currentPhotoURL
                                                    ? <img src={s.currentPhotoURL} alt="" className="w-full h-full object-cover" />
                                                    : s.initials}
                                                {s.avatarUploading && (
                                                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-full">
                                                        <RefreshCw size={18} className="text-white animate-spin" />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-slate-800 dark:text-white mb-2">Gambar profil</p>
                                            <div className="flex items-center gap-2">
                                                <button onClick={() => s.avatarInputRef.current?.click()} disabled={s.avatarUploading}
                                                    className="px-3.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center gap-1.5 disabled:opacity-50">
                                                    <Camera size={13} /> Muat naik
                                                </button>
                                                {s.currentPhotoURL && (
                                                    <button onClick={s.handleRemoveAvatar} disabled={s.avatarUploading}
                                                        className="px-3.5 py-1.5 rounded-lg text-xs font-medium text-slate-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors flex items-center gap-1.5 disabled:opacity-50">
                                                        <Trash2 size={13} /> Buang
                                                    </button>
                                                )}
                                            </div>
                                            <p className="text-[11px] text-slate-400 mt-2">Format imej, maksimum 5MB.</p>
                                        </div>
                                        <input ref={s.avatarInputRef} type="file" accept="image/*" onChange={s.handleAvatarUpload} className="hidden" />
                                    </div>
                                </SettingsCard>

                                <SettingsCard title="Maklumat peribadi" desc="Nama dan cara kami menghubungi anda.">
                                    <div className="space-y-5">

                                        {/* Nama paparan */}
                                        <div>
                                            <div className="flex items-center justify-between mb-1.5">
                                                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">Nama paparan</label>
                                                {!s.isEditingName ? (
                                                    <button onClick={s.startEditName}
                                                        className="flex items-center gap-1 text-[11px] font-medium text-indigo-500 hover:text-indigo-600 transition-colors">
                                                        <Pencil size={11} /> Tukar
                                                    </button>
                                                ) : (
                                                    <div className="flex items-center gap-1.5">
                                                        <button onClick={s.handleSaveName} disabled={s.nameSaving}
                                                            className="flex items-center gap-1 text-[11px] font-semibold text-emerald-600 hover:text-emerald-700 transition-colors disabled:opacity-50">
                                                            {s.nameSaving ? <RefreshCw size={11} className="animate-spin" /> : <Save size={11} />}
                                                            Simpan
                                                        </button>
                                                        <span className="text-slate-300">|</span>
                                                        <button onClick={s.cancelEditName}
                                                            className="flex items-center gap-1 text-[11px] font-medium text-slate-400 hover:text-slate-600 transition-colors">
                                                            <X size={11} /> Batal
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            {!s.isEditingName ? (
                                                <input type="text" value={s.displayName} readOnly
                                                    className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 outline-none cursor-default" />
                                            ) : (
                                                <input type="text" value={s.editName}
                                                    onChange={(e) => s.setEditName(e.target.value)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') s.handleSaveName(); if (e.key === 'Escape') s.cancelEditName(); }}
                                                    autoFocus
                                                    className="w-full rounded-lg border-2 border-indigo-400 dark:border-indigo-500 bg-white dark:bg-slate-800 px-4 py-2.5 text-sm text-slate-800 dark:text-white outline-none ring-2 ring-indigo-500/20 transition-all" />
                                            )}
                                            {s.nameMsg.text && (
                                                <div className={`mt-2 flex items-center gap-1.5 text-xs font-medium ${s.nameMsg.type === 'success' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                                    {s.nameMsg.type === 'success' ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                                                    {s.nameMsg.text}
                                                </div>
                                            )}
                                        </div>

                                        {/* Emel */}
                                        <div>
                                            <div className="flex items-center justify-between mb-1.5">
                                                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">Alamat emel</label>
                                                {!s.isEditingEmail ? (
                                                    <button onClick={s.startEditEmail}
                                                        className="flex items-center gap-1 text-[11px] font-medium text-indigo-500 hover:text-indigo-600 transition-colors">
                                                        <Pencil size={11} /> Tukar
                                                    </button>
                                                ) : (
                                                    <div className="flex items-center gap-1.5">
                                                        <button onClick={s.handleSaveEmail} disabled={s.emailSaving}
                                                            className="flex items-center gap-1 text-[11px] font-semibold text-emerald-600 hover:text-emerald-700 transition-colors disabled:opacity-50">
                                                            {s.emailSaving ? <RefreshCw size={11} className="animate-spin" /> : <Send size={11} />}
                                                            Hantar pengesahan
                                                        </button>
                                                        <span className="text-slate-300">|</span>
                                                        <button onClick={s.cancelEditEmail}
                                                            className="flex items-center gap-1 text-[11px] font-medium text-slate-400 hover:text-slate-600 transition-colors">
                                                            <X size={11} /> Batal
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            {!s.isEditingEmail ? (
                                                <div className="relative">
                                                    <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400"><Mail size={15} /></span>
                                                    <input type="email" value={s.user?.email || ''} readOnly
                                                        className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 pl-10 pr-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 outline-none cursor-default" />
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="relative">
                                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-indigo-400"><Mail size={15} /></span>
                                                        <input type="email" value={s.editEmail}
                                                            onChange={(e) => s.setEditEmail(e.target.value)}
                                                            onKeyDown={(e) => { if (e.key === 'Enter') s.handleSaveEmail(); if (e.key === 'Escape') s.cancelEditEmail(); }}
                                                            autoFocus
                                                            placeholder="masukkan emel baharu"
                                                            className="w-full rounded-lg border-2 border-indigo-400 dark:border-indigo-500 bg-white dark:bg-slate-800 pl-10 pr-4 py-2.5 text-sm text-slate-800 dark:text-white outline-none ring-2 ring-indigo-500/20 transition-all" />
                                                    </div>
                                                    <div className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2 border border-amber-200/50 dark:border-amber-800/50">
                                                        <Info size={12} className="mt-0.5 flex-shrink-0" />
                                                        <span>Emel pengesahan akan dihantar ke alamat baharu. Emel lama kekal berfungsi sehingga anda klik pautan pengesahan. Kata laluan semasa diperlukan.</span>
                                                    </div>
                                                </>
                                            )}
                                            {s.emailMsg.text && (
                                                <div className={`mt-2 flex items-start gap-1.5 text-xs font-medium rounded-lg px-3 py-2 ${s.emailMsg.type === 'success'
                                                    ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200/50 dark:border-emerald-800/50'
                                                    : 'text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/20 border border-rose-200/50 dark:border-rose-800/50'
                                                    }`}>
                                                    {s.emailMsg.type === 'success' ? <CheckCircle size={13} className="mt-0.5 flex-shrink-0" /> : <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />}
                                                    <span>{s.emailMsg.text}</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Nombor WhatsApp */}
                                        <div>
                                            <div className="flex items-center justify-between mb-1.5">
                                                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">Nombor WhatsApp</label>
                                                {!s.isEditingWhatsapp ? (
                                                    <button onClick={s.startEditWhatsapp}
                                                        className="flex items-center gap-1 text-[11px] font-medium text-indigo-500 hover:text-indigo-600 transition-colors">
                                                        <Pencil size={11} /> Tukar
                                                    </button>
                                                ) : (
                                                    <div className="flex items-center gap-1.5">
                                                        <button onClick={s.handleSaveWhatsapp} disabled={s.whatsappSaving}
                                                            className="flex items-center gap-1 text-[11px] font-semibold text-emerald-600 hover:text-emerald-700 transition-colors disabled:opacity-50">
                                                            {s.whatsappSaving ? <RefreshCw size={11} className="animate-spin" /> : <Save size={11} />}
                                                            Simpan
                                                        </button>
                                                        <span className="text-slate-300">|</span>
                                                        <button onClick={s.cancelEditWhatsapp}
                                                            className="flex items-center gap-1 text-[11px] font-medium text-slate-400 hover:text-slate-600 transition-colors">
                                                            <X size={11} /> Batal
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            {!s.isEditingWhatsapp ? (
                                                <div className="relative">
                                                    <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400"><MessageCircle size={15} /></span>
                                                    <input type="tel" value={s.userData?.whatsappNumber || ''} readOnly
                                                        placeholder="Tiada nombor"
                                                        className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 pl-10 pr-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 outline-none cursor-default font-mono" />
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="relative">
                                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-indigo-400"><MessageCircle size={15} /></span>
                                                        <input type="tel" inputMode="numeric" value={s.editWhatsapp}
                                                            onChange={(e) => s.setEditWhatsapp(e.target.value)}
                                                            onKeyDown={(e) => { if (e.key === 'Enter') s.handleSaveWhatsapp(); if (e.key === 'Escape') s.cancelEditWhatsapp(); }}
                                                            autoFocus
                                                            placeholder="Contoh: 0123456789"
                                                            className="w-full rounded-lg border-2 border-indigo-400 dark:border-indigo-500 bg-white dark:bg-slate-800 pl-10 pr-4 py-2.5 text-sm text-slate-800 dark:text-white outline-none ring-2 ring-indigo-500/20 transition-all font-mono" />
                                                    </div>
                                                    <p className="mt-1.5 text-[11px] text-slate-400">
                                                        Nombor untuk akaun SESAAT anda. Untuk nombor yang dipaparkan kepada rujukan affiliate, sila ke halaman Affiliate &gt; Tetapan.
                                                    </p>
                                                </>
                                            )}
                                            {s.whatsappMsg.text && (
                                                <div className={`mt-2 flex items-center gap-1.5 text-xs font-medium ${s.whatsappMsg.type === 'success' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                                    {s.whatsappMsg.type === 'success' ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                                                    {s.whatsappMsg.text}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </SettingsCard>

                                <SettingsCard title="Maklumat akaun">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <InfoRow label="Pakej" tone={s.isAdmin ? 'brand' : s.isPaid ? 'gold' : 'default'}
                                            icon={<TierIcon size={15} className={currentTier.textColor} />}>
                                            <span className={`text-sm font-semibold ${currentTier.textColor}`}>
                                                {currentTier.label}{s.isPaid && s.plan && !s.isAdmin && ` · ${s.plan === 'yearly' ? 'Tahunan' : 'Bulanan'}`}
                                            </span>
                                        </InfoRow>
                                        <InfoRow label="Ahli sejak" icon={<Calendar size={15} className="text-slate-400" />}>
                                            <span className="text-sm text-slate-700 dark:text-slate-300">{s.joinedDate}</span>
                                        </InfoRow>
                                    </div>
                                </SettingsCard>
                            </>
                        )}

                        {/* ================= PAPARAN ================= */}
                        {activeTab === 'appearance' && (
                            <SettingsCard title="Tema" desc="Tetapan ini disimpan pada browser ini sahaja.">
                                <div className="grid grid-cols-2 gap-4">
                                    <button
                                        onClick={() => s.handleThemeChange('light')}
                                        className={`relative flex flex-col items-center justify-center gap-3 py-6 px-4 rounded-xl border-2 transition-all duration-200 group ${!s.darkMode
                                            ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/10 ring-2 ring-indigo-500/20'
                                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                                            }`}
                                    >
                                        <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${!s.darkMode ? 'bg-amber-100 text-amber-500' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 group-hover:text-amber-400'}`}>
                                            <Sun size={24} />
                                        </div>
                                        <span className={`text-sm font-semibold ${!s.darkMode ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400'}`}>Terang</span>
                                        {!s.darkMode && (
                                            <div className="absolute top-2.5 right-2.5 w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center">
                                                <CheckCircle size={12} className="text-white" />
                                            </div>
                                        )}
                                    </button>

                                    <button
                                        onClick={() => s.handleThemeChange('dark')}
                                        className={`relative flex flex-col items-center justify-center gap-3 py-6 px-4 rounded-xl border-2 transition-all duration-200 group ${s.darkMode
                                            ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/10 ring-2 ring-indigo-500/20'
                                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                                            }`}
                                    >
                                        <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${s.darkMode ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-500' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 group-hover:text-indigo-400'}`}>
                                            <Moon size={24} />
                                        </div>
                                        <span className={`text-sm font-semibold ${s.darkMode ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400'}`}>Gelap</span>
                                        {s.darkMode && (
                                            <div className="absolute top-2.5 right-2.5 w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center">
                                                <CheckCircle size={12} className="text-white" />
                                            </div>
                                        )}
                                    </button>
                                </div>
                                <p className="mt-4 flex items-center gap-1.5 text-[11px] text-slate-400">
                                    <Palette size={12} /> Tema tidak mengikut anda ke browser lain — sila set semula di sana.
                                </p>
                            </SettingsCard>
                        )}

                        {/* ================= KATA LALUAN ================= */}
                        {activeTab === 'security' && (
                            <SettingsCard
                                title={s.hasPassword ? 'Tukar kata laluan' : 'Tetapkan kata laluan'}
                                desc={s.hasPassword
                                    ? 'Masukkan kata laluan semasa, kemudian kata laluan baharu.'
                                    : 'Akaun anda belum mempunyai kata laluan.'}
                                footer={
                                    <>
                                        {s.hasPassword && (
                                            <button
                                                onClick={s.handleResetPasswordEmail}
                                                disabled={s.resetEmailLoading || s.resetEmailSent}
                                                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all border ${s.resetEmailSent
                                                    ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-600 cursor-default'
                                                    : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                                                    }`}>
                                                {s.resetEmailLoading ? (
                                                    <><RefreshCw size={14} className="animate-spin" /> Menghantar...</>
                                                ) : s.resetEmailSent ? (
                                                    <><CheckCircle size={14} /> Emel dihantar</>
                                                ) : (
                                                    <><Send size={14} /> Hantar emel tetapan semula</>
                                                )}
                                            </button>
                                        )}
                                        <button
                                            onClick={s.hasPassword ? s.handleChangePassword : s.handleSetPassword}
                                            disabled={!s.canSubmitPassword}
                                            className="px-5 py-2 bg-indigo-500 text-white rounded-lg text-sm font-bold hover:bg-indigo-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                                            {s.passwordLoading ? (
                                                <><RefreshCw size={14} className="animate-spin" /> Menyimpan...</>
                                            ) : (
                                                <><Lock size={14} /> {s.hasPassword ? 'Kemas kini' : 'Tetapkan'}</>
                                            )}
                                        </button>
                                    </>
                                }
                            >
                                <div className="space-y-4">
                                    {/* ★ Akaun Google-sahaja — terangkan kenapa tiada kotak "Kata laluan semasa" */}
                                    {!s.hasPassword && (
                                        <div className="p-3.5 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 flex items-start gap-2.5">
                                            <Info size={14} className="mt-0.5 flex-shrink-0 text-indigo-500" />
                                            <p className="text-xs text-indigo-700 dark:text-indigo-300 leading-relaxed">
                                                Anda log masuk menggunakan butang Google sahaja. Tetapkan kata laluan di bawah — selepas itu anda boleh log masuk menggunakan <strong>{s.user?.email}</strong> dan kata laluan pada mana-mana browser, termasuk Extension SESAAT.
                                            </p>
                                        </div>
                                    )}

                                    {s.hasPassword && (
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Kata laluan semasa</label>
                                            <div className="relative">
                                                <input
                                                    type={s.showPasswords.current ? 'text' : 'password'}
                                                    placeholder="••••••••••••"
                                                    value={s.passwordData.current}
                                                    onChange={(e) => s.setPasswordData(p => ({ ...p, current: e.target.value }))}
                                                    className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 transition-all pr-10"
                                                />
                                                <button type="button" onClick={() => s.setShowPasswords(p => ({ ...p, current: !p.current }))}
                                                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                                                    {s.showPasswords.current ? <EyeOff size={14} /> : <Eye size={14} />}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Kata laluan baharu</label>
                                            <div className="relative">
                                                <input
                                                    type={s.showPasswords.new ? 'text' : 'password'}
                                                    placeholder="Kata laluan baharu"
                                                    value={s.passwordData.new}
                                                    onChange={(e) => s.setPasswordData(p => ({ ...p, new: e.target.value }))}
                                                    className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 transition-all pr-10"
                                                />
                                                <button type="button" onClick={() => s.setShowPasswords(p => ({ ...p, new: !p.new }))}
                                                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                                                    {s.showPasswords.new ? <EyeOff size={14} /> : <Eye size={14} />}
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Sahkan kata laluan baharu</label>
                                            <div className="relative">
                                                <input
                                                    type={s.showPasswords.confirm ? 'text' : 'password'}
                                                    placeholder="Taip semula"
                                                    value={s.passwordData.confirm}
                                                    onChange={(e) => s.setPasswordData(p => ({ ...p, confirm: e.target.value }))}
                                                    className={`w-full rounded-lg border px-4 py-2.5 text-sm outline-none focus:ring-2 transition-all pr-10 ${s.passwordValidation.mismatch
                                                        ? 'border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/10 text-rose-700 dark:text-rose-300 focus:ring-rose-500/30'
                                                        : s.passwordValidation.matches
                                                            ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/10 text-emerald-700 dark:text-emerald-300 focus:ring-emerald-500/30'
                                                            : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 focus:ring-indigo-500/30 focus:border-indigo-400'
                                                        }`}
                                                />
                                                <button type="button" onClick={() => s.setShowPasswords(p => ({ ...p, confirm: !p.confirm }))}
                                                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                                                    {s.showPasswords.confirm ? <EyeOff size={14} /> : <Eye size={14} />}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {s.passwordData.new && (
                                        <div className="space-y-1.5">
                                            <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                                <div className={`h-full rounded-full transition-all duration-500 ease-out ${strengthColor}`} style={{ width: `${s.passwordStrength.pct}%` }}></div>
                                            </div>
                                            <p className={`text-[10px] font-semibold ${s.passwordStrength.score <= 1 ? 'text-rose-500' : s.passwordStrength.score <= 2 ? 'text-orange-500' : s.passwordStrength.score <= 3 ? 'text-amber-500' : 'text-emerald-500'}`}>
                                                Kekuatan: {s.passwordStrength.label}
                                            </p>
                                        </div>
                                    )}

                                    {s.passwordValidation.mismatch && (
                                        <p className="text-xs text-rose-500 flex items-center gap-1"><AlertCircle size={12} /> Kata laluan tidak sepadan.</p>
                                    )}
                                    {s.passwordValidation.matches && (
                                        <p className="text-xs text-emerald-500 flex items-center gap-1"><CheckCircle size={12} /> Kata laluan sepadan.</p>
                                    )}

                                    <p className="text-xs text-slate-400 flex items-center gap-1.5">
                                        <AlertCircle size={12} className="flex-shrink-0" />
                                        Kata laluan mestilah sekurang-kurangnya 6 aksara, dan sebaiknya mengandungi huruf besar serta nombor.
                                    </p>

                                    {s.passwordMsg.text && (
                                        <div className={`p-3.5 rounded-lg text-xs font-medium flex items-start gap-2.5 ${s.passwordMsg.type === 'error'
                                            ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800'
                                            : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800'
                                            }`}>
                                            {s.passwordMsg.type === 'error' ? <AlertCircle size={14} className="mt-0.5 flex-shrink-0" /> : <CheckCircle size={14} className="mt-0.5 flex-shrink-0" />}
                                            <span>{s.passwordMsg.text}</span>
                                        </div>
                                    )}

                                    {s.resetEmailSent && (
                                        <p className="text-[11px] text-slate-400">Sila semak peti masuk (atau folder spam) di <strong>{s.user?.email}</strong>.</p>
                                    )}
                                </div>
                            </SettingsCard>
                        )}

                        {/* ================= PAKEJ SAYA ================= */}
                        {activeTab === 'billing' && (
                            <>
                                <SettingsCard title="Langganan semasa">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <InfoRow label="Pelan semasa" tone={s.isAdmin ? 'brand' : s.isPaid ? 'gold' : 'default'}
                                            icon={<TierIcon size={15} className={currentTier.textColor} />}>
                                            <span className={`text-sm font-semibold ${currentTier.textColor}`}>
                                                {currentTier.label}{s.isPaid && s.plan && !s.isAdmin && ` · ${s.plan === 'yearly' ? 'Tahunan' : 'Bulanan'}`}
                                            </span>
                                        </InfoRow>
                                        <InfoRow label="Aktif sehingga" icon={<Clock size={15} className="text-slate-400" />}>
                                            <span className="text-sm text-slate-700 dark:text-slate-300">
                                                {s.isAdmin ? 'Tanpa had' : (s.expiryDate || 'Tiada rekod')}
                                            </span>
                                        </InfoRow>
                                    </div>
                                </SettingsCard>

                                {/* Slot browser + slot avatar. SATU nombor lesen (maxProfiles)
                                    mengawal kedua-duanya — dipapar bersebelahan supaya pelanggan
                                    nampak ia satu kuota, bukan dua yang berasingan. */}
                                <SettingsCard title="Slot anda" desc="Satu kuota untuk kedua-duanya. Setiap browser Chrome yang anda log masuk mengambil satu slot, begitu juga setiap avatar yang anda simpan.">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        <div>
                                            <div className="text-xs text-slate-500 dark:text-slate-400 mb-2">Browser Chrome</div>
                                            {slotTanpaHad ? (
                                                <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                                                    {browserDigunakan} sedang digunakan · tanpa had
                                                </span>
                                            ) : (
                                                <>
                                                    <div className="flex items-baseline justify-between mb-2.5">
                                                        <span className="text-2xl font-bold text-slate-900 dark:text-white">{browserDigunakan}</span>
                                                        <span className="text-sm text-slate-400">daripada {kuotaSlot} digunakan</span>
                                                    </div>
                                                    <UsageBar ratio={kuotaSlot > 0 ? browserDigunakan / kuotaSlot : 0} />
                                                </>
                                            )}
                                        </div>
                                        <div>
                                            <div className="text-xs text-slate-500 dark:text-slate-400 mb-2">Avatar disimpan</div>
                                            {/* Bilangan avatar boleh gagal dibaca. Papar "—", bukan 0 —
                                                melaporkan 0 kepada pelanggan yang sebenarnya ada avatar
                                                adalah lebih buruk daripada tidak melapor langsung. */}
                                            {avatarDigunakan === null || avatarDigunakan === undefined ? (
                                                <span className="text-sm text-slate-400">Tidak dapat dibaca sekarang</span>
                                            ) : (
                                                <>
                                                    <div className="flex items-baseline justify-between mb-2.5">
                                                        <span className="text-2xl font-bold text-slate-900 dark:text-white">{avatarDigunakan}</span>
                                                        <span className="text-sm text-slate-400">daripada {avatarKuota} digunakan</span>
                                                    </div>
                                                    <UsageBar ratio={avatarKuota > 0 ? avatarDigunakan / avatarKuota : 0} />
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    {berbayarBukanAdmin && (
                                        <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
                                            Perlu slot tambahan? Hubungi kami di WhatsApp.
                                        </p>
                                    )}
                                </SettingsCard>

                                {/* Modul yang pelanggan betul-betul ada. Nama pemasaran, bukan id
                                    dalaman — 'ai16saat-half' tidak bermakna apa-apa kepada pelanggan. */}
                                <SettingsCard title="Modul anda" desc="Modul yang terbuka pada akaun anda.">
                                    {modulSaya.length ? (
                                        <div className="flex flex-wrap gap-2">
                                            {modulSaya.map((m) => (
                                                <span key={m.id}
                                                    className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                                                    {m.nama}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-slate-500 dark:text-slate-400">Belum ada modul yang dibuka pada akaun ini.</p>
                                    )}
                                </SettingsCard>

                                {s.isPaid && !s.isAdmin && (
                                    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                                        <button onClick={() => navigate('/pricing')} className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-lg bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center">
                                                    <CreditCard size={16} className="text-amber-500" />
                                                </div>
                                                <div className="text-left">
                                                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-300 block">Urus langganan</span>
                                                    <span className="text-[11px] text-slate-400">Perbaharui atau tukar pelan anda</span>
                                                </div>
                                            </div>
                                            <ChevronRight size={16} className="text-slate-400" />
                                        </button>
                                    </div>
                                )}

                                {/* CTA untuk pelanggan belum berbayar. Ayat merujuk produk SESAAT
                                    yang sebenar — versi lama tertulis "unlimited rewrites, 10x
                                    transcriptions" yang bukan produk kita langsung. */}
                                {!s.isPaid && (
                                    <SettingsCard tone="warn">
                                        <div className="flex items-start gap-3 mb-3">
                                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                                                <Sparkles size={19} className="text-white" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-sm text-slate-800 dark:text-white">Naik taraf ke pelan berbayar</h4>
                                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                                    Buka semua modul, kuota {DAILY_PAID_CAP} generation sehari, dan automasi Flow serta TikTok tanpa had percubaan.
                                                </p>
                                            </div>
                                        </div>
                                        <button onClick={() => navigate('/pricing')}
                                            className="w-full px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-bold hover:from-amber-600 hover:to-orange-600 transition-all flex items-center justify-center gap-1">
                                            Lihat harga <ChevronRight size={14} />
                                        </button>
                                    </SettingsCard>
                                )}
                            </>
                        )}

                        {/* ================= STORAGE & KUOTA ================= */}
                        {activeTab === 'storage' && (
                            <>
                                <SettingsCard title="Ruang simpanan" desc="Video dan gambar yang disimpan ke Cloud.">
                                    <div className="flex items-baseline justify-between mb-2.5">
                                        <span className="text-2xl font-bold text-slate-900 dark:text-white">{fmtBytes(s.storageUsed)}</span>
                                        <span className="text-sm text-slate-400">daripada {fmtBytes(STORAGE_LIMIT_BYTES)}</span>
                                    </div>
                                    <UsageBar ratio={s.storageRatio} />
                                    <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                                        {s.storageRatio > 0.9
                                            ? 'Ruang hampir penuh. Video baharu mungkin gagal disimpan sehingga anda melapangkan ruang.'
                                            : 'Padam video yang sudah dimuat naik ke TikTok untuk melapangkan ruang.'}
                                    </p>
                                    <button onClick={() => navigate('/workspace')}
                                        className="mt-4 w-full px-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center justify-center gap-2">
                                        <HardDrive size={15} /> Urus video di Workspace
                                    </button>
                                </SettingsCard>

                                <SettingsCard title="Kuota generation" desc="Satu generation menghasilkan 3 set prompt.">
                                    {s.isAdmin ? (
                                        <InfoRow label="Hari ini" tone="brand" icon={<Gauge size={15} className="text-indigo-500" />}>
                                            <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                                                {s.usedToday} digunakan · tanpa had
                                            </span>
                                        </InfoRow>
                                    ) : s.isPaid ? (
                                        <>
                                            <div className="flex items-baseline justify-between mb-2.5">
                                                <span className="text-2xl font-bold text-slate-900 dark:text-white">{s.usedToday}</span>
                                                <span className="text-sm text-slate-400">daripada {DAILY_PAID_CAP} hari ini</span>
                                            </div>
                                            <UsageBar ratio={s.usedToday / DAILY_PAID_CAP} />
                                            <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                                                Kuota kembali penuh pada pukul 12:00 tengah malam (waktu Malaysia). Kuota ini dikongsi antara semua modul.
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <div className="flex items-baseline justify-between mb-2.5">
                                                <span className="text-2xl font-bold text-slate-900 dark:text-white">{s.trialCoins ?? 0}</span>
                                                <span className="text-sm text-slate-400">daripada {TRIAL_COIN_CAP} percubaan</span>
                                            </div>
                                            <UsageBar ratio={1 - ((s.trialCoins ?? 0) / TRIAL_COIN_CAP)} />
                                            <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                                                Kuota percubaan diberi sekali sahaja dan tidak diisi semula setiap hari.
                                            </p>
                                        </>
                                    )}
                                </SettingsCard>
                            </>
                        )}

                        {/* ================= EXTENSION ================= */}
                        {activeTab === 'extension' && (
                            <>
                                <SettingsCard
                                    title="Status pada browser ini"
                                    desc="Setiap browser perlu memasang extension secara berasingan."
                                    footer={
                                        <button onClick={s.runExtProbe} disabled={s.extState.status === 'checking'}
                                            className="px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center gap-2 disabled:opacity-50">
                                            <RefreshCw size={14} className={s.extState.status === 'checking' ? 'animate-spin' : ''} />
                                            Semak semula
                                        </button>
                                    }
                                >
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <InfoRow label="Status"
                                            icon={s.extState.status === 'checking'
                                                ? <RefreshCw size={15} className="text-slate-400 animate-spin" />
                                                : s.extState.status === 'connected'
                                                    ? <CheckCircle size={15} className="text-emerald-500" />
                                                    : <AlertCircle size={15} className="text-slate-400" />}>
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                {s.extState.status === 'checking' ? 'Sedang menyemak...'
                                                    : s.extState.status === 'connected' ? 'Tersambung'
                                                        : 'Tidak dikesan'}
                                            </span>
                                        </InfoRow>
                                        <InfoRow label="Versi dipasang" icon={<Puzzle size={15} className="text-slate-400" />}>
                                            <span className="text-sm font-mono text-slate-700 dark:text-slate-300">
                                                {s.extState.status === 'connected' ? (s.extState.version || 'Versi lama') : '—'}
                                            </span>
                                        </InfoRow>
                                    </div>

                                    <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
                                        Versi terkini yang diedarkan: <strong className="font-mono">{APP_VERSION}</strong>
                                    </p>

                                    {/* Dua extension serentak — punca aduan "saya dah update tapi
                                        masih kata versi lama". Yang lama WAJIB dibuang. */}
                                    {s.extState.responders > 1 && (
                                        <div className="mt-4 p-3.5 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 flex items-start gap-2.5">
                                            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0 text-amber-500" />
                                            <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
                                                Browser ini mempunyai <strong>lebih daripada satu</strong> Extension SESAAT yang aktif. Sila buka <span className="font-mono">chrome://extensions</span> dan buang pemasangan yang lama — dua pemasangan serentak boleh menyebabkan automasi berkelakuan pelik.
                                            </p>
                                        </div>
                                    )}

                                    {s.extState.status === 'missing' && (
                                        <div className="mt-4 p-3.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex items-start gap-2.5">
                                            <Info size={14} className="mt-0.5 flex-shrink-0 text-slate-400" />
                                            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                                                Extension tidak menjawab pada browser ini. Ia mungkin belum dipasang, atau baru dipasang tetapi halaman ini belum dimuat semula.
                                            </p>
                                        </div>
                                    )}
                                </SettingsCard>

                                <SettingsCard title="Muat turun & pemasangan">
                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <a href={EXTENSION_ZIP_URL} download
                                            className="flex-1 px-4 py-2.5 bg-indigo-500 text-white rounded-lg text-sm font-bold hover:bg-indigo-600 transition-all flex items-center justify-center gap-2">
                                            <Download size={15} /> Muat turun extension
                                        </a>
                                        <button onClick={() => navigate('/extension')}
                                            className="flex-1 px-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center justify-center gap-2">
                                            Cara pasang <ChevronRight size={14} />
                                        </button>
                                    </div>
                                    <p className="mt-3 text-[11px] text-slate-400">
                                        SESAAT tidak diedarkan melalui Chrome Web Store. Selepas memasang versi baharu, muat semula halaman ini supaya status di atas dikemas kini.
                                    </p>
                                </SettingsCard>
                            </>
                        )}

                        {/* ================= EJEN RUJUKAN ================= */}
                        {activeTab === 'referral' && (
                            <SettingsCard title="Ejen rujukan anda">
                                {s.loadingReferrer ? (
                                    <div className="flex items-center gap-2 text-xs text-slate-400">
                                        <RefreshCw size={14} className="animate-spin" />
                                        <span>Memuatkan maklumat ejen...</span>
                                    </div>
                                ) : s.referrerInfo ? (
                                    <div className="space-y-4">
                                        <InfoRow label="Nama ejen" icon={<User size={15} className="text-slate-400" />}>
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                {s.referrerInfo.name || s.referrerInfo.email?.split('@')[0] || 'Ejen Affiliate'}
                                            </span>
                                        </InfoRow>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Kod rujukan</label>
                                                <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20">
                                                    <Sparkles size={15} className="text-purple-500" />
                                                    <span className="text-sm font-mono font-bold text-purple-700 dark:text-purple-300">
                                                        {s.referrerInfo.refCode}
                                                    </span>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Hubungi ejen</label>
                                                {s.referrerInfo.whatsappNumber ? (
                                                    <a
                                                        href={`https://wa.me/${String(s.referrerInfo.whatsappNumber).replace(/\D/g, '')}`}
                                                        target="_blank" rel="noopener noreferrer"
                                                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors"
                                                    >
                                                        <MessageCircle size={15} className="text-emerald-500" />
                                                        <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                                                            +{String(s.referrerInfo.whatsappNumber).replace(/\D/g, '')}
                                                        </span>
                                                    </a>
                                                ) : (
                                                    <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
                                                        <MessageCircle size={15} className="text-slate-300" />
                                                        <span className="text-xs text-slate-400 italic">Tiada nombor</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                                            Sekiranya maklumat ejen ini tidak tepat, sila hubungi admin SESAAT untuk semakan.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                        <p className="text-sm text-slate-600 dark:text-slate-300 mb-1">Tiada ejen rujukan</p>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                            Anda mendaftar secara terus tanpa pautan affiliate. Sekiranya anda klik pautan ejen tetapi maklumatnya tidak dipaparkan di sini, sila hubungi admin untuk semakan.
                                        </p>
                                    </div>
                                )}
                            </SettingsCard>
                        )}
                    </div>

                    <p className="text-center text-[10px] text-slate-300 dark:text-slate-700 mt-10">
                        © 2026 SESAAT.AI · AI Automation Studio
                    </p>
                </div>
            </main>

            {/* ===== MODAL PENGESAHAN SEMULA ===== */}
            {s.showReauthModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
                    <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800"
                        style={{ animation: 'fadeIn 0.2s ease-out' }}>
                        <div className="px-6 pt-6 pb-4 border-b border-slate-100 dark:border-slate-800">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                                    <Shield size={20} className="text-indigo-500" />
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-slate-900 dark:text-white">Pengesahan keselamatan</h3>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Masukkan kata laluan semasa anda untuk meneruskan</p>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 py-5">
                            <div className="mb-4 flex items-start gap-2 text-xs text-slate-500 bg-slate-50 dark:bg-slate-800/50 rounded-lg px-3 py-2.5 border border-slate-100 dark:border-slate-800">
                                <Info size={13} className="mt-0.5 flex-shrink-0 text-indigo-400" />
                                <span>
                                    {s.reauthAction === 'email' && (
                                        <>Menukar emel kepada <strong className="text-slate-700 dark:text-slate-300">{s.editEmail}</strong>. Emel pengesahan akan dihantar ke alamat baharu.</>
                                    )}
                                </span>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Kata laluan semasa</label>
                                <div className="relative">
                                    <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400"><Lock size={15} /></span>
                                    <input
                                        type="password"
                                        value={s.reauthPassword}
                                        onChange={(e) => s.setReauthPassword(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') s.handleReauthAndExecute(); if (e.key === 'Escape') s.closeReauthModal(); }}
                                        placeholder="Masukkan kata laluan anda"
                                        autoFocus
                                        className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 pl-10 pr-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 transition-all"
                                    />
                                </div>
                            </div>

                            {s.reauthError && (
                                <div className="mt-3 flex items-center gap-1.5 text-xs font-medium text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/20 rounded-lg px-3 py-2 border border-rose-200/50 dark:border-rose-800/50">
                                    <AlertCircle size={12} className="flex-shrink-0" />
                                    {s.reauthError}
                                </div>
                            )}
                        </div>

                        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/30 flex items-center justify-end gap-3 border-t border-slate-100 dark:border-slate-800">
                            <button
                                onClick={s.closeReauthModal}
                                className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                                Batal
                            </button>
                            <button
                                onClick={s.handleReauthAndExecute}
                                disabled={s.reauthLoading || !s.reauthPassword}
                                className="px-5 py-2 bg-indigo-500 text-white rounded-lg text-sm font-semibold hover:bg-indigo-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg shadow-indigo-500/20">
                                {s.reauthLoading ? (
                                    <><RefreshCw size={14} className="animate-spin" /> Mengesahkan...</>
                                ) : (
                                    <><ShieldCheck size={14} /> Sahkan dan teruskan</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfileView;
